import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { Sprite, Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import anime from "animejs";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setAutoplaySpeed,
  closeMenu,
} from "../../../../State/actions";
import {
  selectAnyPopupActive,
  selectSessionExpired,
  selectIsSpinning,
  selectAutoPlayActive,
  selectHoldAndWinActive,
  selectLastCommand,
  selectFeatureState,
  selectFlowState,
  selectWindupComplete,
  selectErrorPopupActive,
  selectInfoPopupActive,
  selectSpinComplete,
  selectFreeSpinActive,
  selectFreeSpinTriggered,
  selectRoyalCollectActive,
  selectRoyalCollectTriggered,
} from "../../../../State/selectors";
import { selectRemainingAutoSpins } from "../../../../State/Slices/settingsSlice";
import store from "../../../../State/Store/store";

import { createButtonPressAnimation } from "../../../../utilities";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { FONT_CONFIG, TEXT_STYLE_CONFIG } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Play button configuration - matches UI button dimensions
const PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.65,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.65,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.35,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.75,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.4,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 0.9,
    },
  },
};

// Position config — extracted for easy tweaking
// Values are multipliers applied to toolbar/buttonConfig/dimensions
const PLAY_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width / 2,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 10.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.65,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height / 2.0,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width / 2,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 10.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.65,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 + buttonConfig.height / 2.0,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.6,
      y: (toolbar, buttonConfig, dimensions) =>
        toolbar.height - buttonConfig.height * 2.2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width / 1.6,
      y: (toolbar, buttonConfig) => toolbar.height - buttonConfig.height * 3.2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.275,
    y: (toolbar, buttonConfig, dimensions, uiSymbolSize) =>
      buttonConfig.width * 0.1,
  },
};

// Input state tracking for spacebar and touch interactions
let spacePressed = false;
let spaceStartTime = 0;
let holdTimer = null;
let lastActionTime = 0;
const MIN_ACTION_GAP = 50; // Minimum gap between actions (ms) — 20 actions/sec max

// Touch state variables mirroring spacebar logic
let touchPressed = false;
let touchStartTime = 0;
let touchHoldTimer = null;
let touchId = null;

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let clickThrottleActive = false;
let clickThrottleTimeout = null;

const ButtonPlay = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [actionInProgress, setActionInProgress] = useState(false);
  const [stopButtonDisabled, setStopButtonDisabled] = useState(false);

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);
  // Track if press animation is currently running (prevents rapid click shrinking)

  // Single shared timeout for flash effect (prevents buildup)
  const flashTimeoutRef = useRef(null);
  const { playButtonRef } = useReelRefs();
  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig } =
    useDimensions();

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Refs for latest state access in event handlers
  const setActionInProgressRef = useRef(setActionInProgress);
  const setStopButtonDisabledRef = useRef(setStopButtonDisabled);

  const {
    handleSpin,
    handleManualStop,
    startSpacebarTurbo,
    stopSpacebarTurbo,
    spacebarTurboActive,
    pendingTensionRef,
    turboTensionEjectedRef,
  } = useReelControl();

  // Redux state selectors
  const sessionExpired = useSelector(selectSessionExpired);
  const isSpinning = useSelector(selectIsSpinning);
  const windupComplete = useSelector(selectWindupComplete);

  const autoPlayActive = useSelector(selectAutoPlayActive);
  const spinComplete = useSelector(selectSpinComplete);
  const remainingAutoSpins = useSelector(selectRemainingAutoSpins);

  const { winAnimationsPlaying, criticalAnimationPlaying } = useSelector(
    (state) => state.animations,
  );
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const lastCommand = useSelector(selectLastCommand);
  const featureState = useSelector(selectFeatureState);
  const flowState = useSelector(selectFlowState); // Legacy fallback

  const anyPopupActive = useSelector(selectAnyPopupActive);

  const errorPopupActive = useSelector(selectErrorPopupActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const freeSpinTriggered = useSelector(selectFreeSpinTriggered);
  const royalCollectActive = useSelector(selectRoyalCollectActive);
  const royalCollectTriggered = useSelector(selectRoyalCollectTriggered);

  // Multi-feature visibility:
  // - freeSpinActive or royalCollectActive: fully hidden (0.001) — feature play button replaces it
  // - freeSpinTriggered/royalCollectTriggered + spinComplete: disabled look (0.5)
  // - otherwise: fully visible (1)
  const featureButtonAlpha = (freeSpinActive || royalCollectActive)
    ? 0.001
    : (freeSpinTriggered || royalCollectTriggered) && spinComplete
      ? 0.5
      : 1;
  const featureButtonInteractive = !freeSpinActive && !freeSpinTriggered && !royalCollectActive && !royalCollectTriggered;

  // Refs for latest values
  const spacebarTurboActiveRef = useRef(spacebarTurboActive);
  const stopSpacebarTurboRef = useRef(stopSpacebarTurbo);
  const startSpacebarTurboRef = useRef(startSpacebarTurbo);
  const handleSpinRef = useRef(handleSpin);
  const handleManualStopRef = useRef(handleManualStop);

  const actionInProgressRef = useRef(actionInProgress);
  const stopButtonDisabledRef = useRef(stopButtonDisabled);
  const isSpinningRef = useRef(isSpinning);
  const windupCompleteRef = useRef(windupComplete);


  // Action gating to prevent rapid clicks
  // Controls which action type is currently allowed: 'SPIN', 'STOP', or null (no action)
  const allowedActionRef = useRef(null);

  // Pending stop mechanism - allows user to queue a stop during windup
  // User can click SPIN then immediately STOP, but stop waits for windup to complete
  const pendingStopRef = useRef(false);

  // Update refs when values change
  useEffect(() => {
    spacebarTurboActiveRef.current = spacebarTurboActive;
    stopSpacebarTurboRef.current = stopSpacebarTurbo;
    startSpacebarTurboRef.current = startSpacebarTurbo;
    handleSpinRef.current = handleSpin;
    handleManualStopRef.current = handleManualStop;

    actionInProgressRef.current = actionInProgress;
    stopButtonDisabledRef.current = stopButtonDisabled;
    isSpinningRef.current = isSpinning;
    windupCompleteRef.current = windupComplete;
    setActionInProgressRef.current = setActionInProgress;
    setStopButtonDisabledRef.current = setStopButtonDisabled;
  }, [
    spacebarTurboActive,
    stopSpacebarTurbo,
    startSpacebarTurbo,
    handleSpin,
    handleManualStop,

    actionInProgress,
    stopButtonDisabled,
    setActionInProgress,
    setStopButtonDisabled,
    isSpinning,
    windupComplete,
  ]);



  // Button state

  // Initialize action gate on mount
  useEffect(() => {
    // On mount, if not spinning, allow spin
    if (!isSpinning) {
      allowedActionRef.current = "SPIN";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - isSpinning checked but not in deps intentionally

  // Action gate management - controls which action is allowed at each phase
  // Prevents rapid clicks by blocking actions that don't match the current game phase
  useEffect(() => {
    // When windup completes and we're spinning, allow STOP action
    if (isSpinning && windupComplete) {
      allowedActionRef.current = "STOP";
      // NOTE: pending stop is handled by direct store subscription (pendingStopUnsubRef)
      // for near-instant execution — no React render cycle delay
    }
  }, [isSpinning, windupComplete]);

  // Direct store subscription ref for pending stop — fires synchronously on state change,
  // bypassing React's render cycle for ~32ms faster response
  const pendingStopUnsubRef = useRef(null);

  // Helper: queue a pending stop with direct store subscription for instant execution
  const queuePendingStop = useCallback(() => {
    if (pendingStopRef.current) return; // Already queued
    pendingStopRef.current = true;

    // Clean up any existing subscription
    if (pendingStopUnsubRef.current) {
      pendingStopUnsubRef.current();
      pendingStopUnsubRef.current = null;
    }

    // Subscribe directly to Redux store — fires synchronously on dispatch,
    // no React render cycle needed
    pendingStopUnsubRef.current = store.subscribe(() => {
      const s = store.getState();
      if (s.spin.windupComplete && s.spin.isSpinning) {
        // Unsubscribe immediately to prevent double-fire
        if (pendingStopUnsubRef.current) {
          pendingStopUnsubRef.current();
          pendingStopUnsubRef.current = null;
        }
        pendingStopRef.current = false;
        allowedActionRef.current = null; // Lock immediately
        handleManualStopRef.current(); // Execute the queued stop
      }
    });
  }, []);

  // When spin completes, allow SPIN action for next spin (ONLY in manual mode, NOT autoplay)
  useEffect(() => {
    if (!isSpinning) {
      if (!autoPlayActive) {
        // Manual mode: Allow SPIN for next spin
        allowedActionRef.current = "SPIN";
        pendingStopRef.current = false; // Clear any pending stop
      } else {
        // Autoplay mode: Keep gate NULL, spins are triggered automatically by ReelControlContext
        allowedActionRef.current = null;
        pendingStopRef.current = false; // Clear any pending stop
      }
      // Clean up pending stop subscription when spin ends
      if (pendingStopUnsubRef.current) {
        pendingStopUnsubRef.current();
        pendingStopUnsubRef.current = null;
      }
    }
  }, [isSpinning, autoPlayActive]);

  // Manage button enabled/disabled state based on game phase
  // Button must stay disabled during windup for ALL spins (manual + autoplay)
  // This prevents clicking before reels are actually spinning, which causes infinite spin bug
  useEffect(() => {
    // When spin completes, reset actionInProgress and re-enable button for next action
    if (!isSpinning && actionInProgress) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      return;
    }

    // Wait for windup to complete before allowing stop
    // Button re-enables when: spin is active AND windup is complete
    // If actionInProgress is true, it means we already queued/executed a stop, so keep it disabled
    if (isSpinning && windupComplete && !actionInProgress) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      return;
    }
  }, [actionInProgress, isSpinning, autoPlayActive, windupComplete]);

  // ONLY reset button state and stop turbo for BIG WIN animations
  // Counter animations and expanding wilds should NOT reset button state
  // This allows turbo to resume after counter finishes if button still held
  useEffect(() => {
    if (winAnimationsPlaying) {
      // Clean up touch state completely for big win animations
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clean up spacebar state completely for big win animations
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Stop turbo completely for big win animations
      if (spacebarTurboActive) {
        const currentState = store.getState();
        if (currentState.settings.autoPlaySpeed === "turbo") {
          dispatch(setAutoplaySpeed("auto"));
        }
        stopSpacebarTurboRef.current();
      }
    }
  }, [winAnimationsPlaying, spacebarTurboActive, dispatch]);

  // CRITICAL: Clear hold state when critical animation starts (Hold and Win detection ONLY)
  // This prevents button release from triggering a spin when Hold and Win is detected
  // User might be holding button when Hold and Win triggers, so we clear hold state immediately
  // NOTE: Expanding wilds also set criticalAnimationPlaying, but we DON'T clear hold state for those
  // (they should work like regular wins - turbo continues after counter finishes)
  useEffect(() => {
    if (criticalAnimationPlaying) {
      // Check if this is for Hold and Win (not expanding wilds)
      // Use universal featureState if available, fall back to legacy flowState
      const isHoldAndWinTrigger =
        FEATURES_CONFIG.HOLD_AND_WIN.ENABLED &&
        ((featureState &&
          featureState.type === "holdAndWin" &&
          featureState.phase === "intro") ||
          (lastCommand === "spin" && flowState === "respin"));

      // Only clear hold state for Hold and Win, NOT for expanding wilds
      if (isHoldAndWinTrigger || holdAndWinActive) {
        // Clean up touch state completely when Hold and Win starts
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }
        touchPressed = false;
        touchStartTime = 0;
        touchId = null;

        // Clean up spacebar state completely when Hold and Win starts
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        spacePressed = false;
        spaceStartTime = 0;

        // Stop turbo if active when Hold and Win starts
        if (spacebarTurboActive) {
          const currentState = store.getState();
          if (currentState.settings.autoPlaySpeed === "turbo") {
            dispatch(setAutoplaySpeed("auto"));
          }
          stopSpacebarTurboRef.current();
        }
      }
      // If it's expanding wilds (criticalAnimationPlaying but NOT Hold and Win),
      // do nothing - let turbo continue naturally after counter finishes
    }
  }, [
    criticalAnimationPlaying,
    spacebarTurboActive,
    dispatch,
    holdAndWinActive,
    featureState,
    lastCommand,
    flowState,
  ]);

  // Safety Effect: Stop turbo and clear hold state when error/info popups appear
  // Prevents turbo spins from continuing when critical popups are displayed (e.g., free rounds ending)
  // Similar to how turbo stops for win animations, this ensures users can't bypass important messages
  const previousPopupFlagsRef = useRef({
    errorPopupActive: false,
    infoPopupActive: false,
  });

  useEffect(() => {
    const errorPopupJustOpened =
      errorPopupActive && !previousPopupFlagsRef.current.errorPopupActive;
    const infoPopupJustOpened =
      infoPopupActive && !previousPopupFlagsRef.current.infoPopupActive;

    // Stop turbo and clear hold state if any popup just opened
    if ((errorPopupJustOpened || infoPopupJustOpened) && spacebarTurboActive) {
      // Clean up touch state completely for popups
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clean up spacebar state completely for popups
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Stop turbo completely for popups
      if (spacebarTurboActive) {
        const currentState = store.getState();
        if (currentState.settings.autoPlaySpeed === "turbo") {
          dispatch(setAutoplaySpeed("auto"));
        }
        stopSpacebarTurboRef.current();
      }
    }

    previousPopupFlagsRef.current = {
      errorPopupActive,
      infoPopupActive,
    };
  }, [errorPopupActive, infoPopupActive, spacebarTurboActive, dispatch]);

  // ============================================================================
  // CENTRALIZED ACTION RESOLUTION SYSTEM
  // ============================================================================

  // Determine if turbo mode should be allowed to START (no animations active)
  // Note: This is for STARTING turbo, not for continuing it after counter animations
  const shouldAllowTurbo = useCallback(() => {
    const state = store.getState();
    const counterIsShowing =
      state.animations.counterAnimationStarted &&
      !state.animations.counterAnimationFinished;

    return !(state.animations.winAnimationsPlaying || counterIsShowing);
  }, []);

  // Resolve what action the button should perform
  const resolveButtonAction = useCallback(() => {
    const state = store.getState();
    const autoPlayActive = state.settings.autoPlayActive;
    const autoPlaySpeed = state.settings.autoPlaySpeed;

    // Priority 1: Skip animations if they're active
    if (state.animations.winAnimationsPlaying) {
      return {
        type: "SKIP_ANIMATIONS",
        winAnimationsPlaying: state.animations.winAnimationsPlaying,
        isWaitingForConfirmation: state.animations.isWaitingForUserConfirmation,
      };
    }

    // Priority 2: Block ALL actions if autoplay is active AND speed is turbo or quick
    // Quick and turbo spins are too fast for manual stopping to be useful
    if (
      autoPlayActive &&
      (autoPlaySpeed === "turbo" || autoPlaySpeed === "quick")
    ) {
      return {
        type: "NO_OP",
      };
    }

    // Priority 3: Stop spin if spinning
    // CRITICAL: During autoplay, if manual stop is already pending, don't allow another stop
    if (state.spin.isSpinning) {
      if (autoPlayActive && state.spin.manualStop) {
        // Manual stop already triggered, don't allow another one
        return {
          type: "NO_OP",
        };
      }
      return {
        type: "STOP_SPIN",
        autoPlayActive: autoPlayActive,
        autoPlaySpeed: autoPlaySpeed,
      };
    }

    // Priority 4: Block starting new spin if autoplay is active
    if (autoPlayActive) {
      return {
        type: "NO_OP",
      };
    }

    // Priority 5: Start spin
    return {
      type: "START_SPIN",
    };
  }, []);

  // Execute skip animations action
  const executeSkipAnimations = useCallback(
    (actionData) => {
      soundManager.play(SOUND_NAME.button_click);

      if (actionData.winAnimationsPlaying) {
        if (actionData.isWaitingForConfirmation) {
          dispatch(setUserWantsToDismissWinAnimation(true));
        } else {
          dispatch(setSkipWinAnimation(true));
        }
      }
    },
    [dispatch],
  );

  // Execute stop spin action
  const executeStopSpin = useCallback(
    (actionData) => {
      // Regular reel stop - check turbo/quick restrictions
      // Don't allow manual stop in turbo or quick mode (non-autoplay)
      if (
        !actionData.autoPlayActive &&
        (actionData.autoPlaySpeed === "turbo" ||
          actionData.autoPlaySpeed === "quick")
      ) {
        return false;
      }

      // Don't allow manual stop in autoplay turbo/quick mode (already blocked in resolveButtonAction, but double-check)
      if (
        actionData.autoPlayActive &&
        (actionData.autoPlaySpeed === "turbo" ||
          actionData.autoPlaySpeed === "quick")
      ) {
        return false;
      }

      // Block ALL actions until spin completes
      allowedActionRef.current = null;

      // Press animation on meaningful action
      if (playButtonRef.current) {
        createButtonPressAnimation(playButtonRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
        });
      }

      soundManager.play(SOUND_NAME.button_click);
      lastActionTime = Date.now();
      setActionInProgressRef.current(true);
      setStopButtonDisabledRef.current(true);
      handleManualStopRef.current();

      return true;
    },
    [playButtonRef, buttonConfig],
  );

  // Execute start spin action
  const executeStartSpin = useCallback(() => {
    const state = store.getState();

    // Block manual spin if autoplay is active
    // Autoplay manages its own spin cycle - manual spins should not interfere
    if (state.settings.autoPlayActive) {
      return false;
    }

    // Block ALL actions during windup phase
    allowedActionRef.current = null;

    // Allow spin regardless of balance - API will handle insufficient credits

    soundManager.play(SOUND_NAME.button_click);
    lastActionTime = Date.now();
    actionInProgressRef.current = true;
    handleSpinRef.current();

    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playButtonRef, buttonConfig]);

  // Flash effect on click/tap (visual feedback) — no-op now, kept for API compatibility
  const flashBrightness = useCallback(() => {}, []);

  // Master action executor
  const executeButtonAction = useCallback(
    (action, source = "click") => {
      // Visual feedback already handled at top of handleClick and handleKeyDown
      // No need to add it here anymore

      switch (action.type) {
        case "SKIP_ANIMATIONS":
          executeSkipAnimations(action);
          break;
        case "STOP_SPIN":
          executeStopSpin(action);
          flashBrightness();
          break;
        case "START_SPIN":
          executeStartSpin();
          flashBrightness();
          break;
        case "NO_OP":
          // Do nothing - autoplay is active, just ignore the click
          break;
        default:
          // Unknown action type - ignore
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executeSkipAnimations, executeStopSpin, executeStartSpin],
  );

  // ============================================================================
  // SIMPLIFIED EVENT HANDLERS
  // ============================================================================

  // Handle click function - now uses centralized action resolution
  const handleClick = useCallback(() => {
    // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
    // Prevents performance degradation from rapid clicking (50+ clicks/sec)
    if (clickThrottleActive) {
      return; // Instant exit - zero overhead, no logs, no code execution
    }

    // Set throttle flag IMMEDIATELY and schedule single reset
    clickThrottleActive = true;
    if (clickThrottleTimeout) {
      clearTimeout(clickThrottleTimeout);
    }
    clickThrottleTimeout = setTimeout(() => {
      clickThrottleActive = false;
      clickThrottleTimeout = null;
    }, MIN_ACTION_GAP);

    // EARLY TURBO CHECK: Skip all expensive work if turbo mode is active
    // This check happens before state reads, action resolution, and animations
    const quickState = store.getState();

    // During active tension, skip is handled by pointerDown/keyDown — exit silently
    // Prevents double-firing on desktop where both pointerdown and click fire
    if (pendingTensionRef?.current && quickState.spin.isSpinning) {
      return;
    }

    if (
      quickState.settings.autoPlayActive &&
      (quickState.settings.autoPlaySpeed === "turbo" ||
        quickState.settings.autoPlaySpeed === "quick")
    ) {
      return; // Exit immediately - no visual feedback, no expensive work
    }

    // Determine what action will be taken
    const action = resolveButtonAction();

    // Close menu if open and attempt fullscreen toggle
    dispatch(closeMenu());


    // Block clicks if there's a win that hasn't been fully displayed yet
    // Only applies when counterAnimationStarted is true — animated wins (Big Win, H&W trigger)
    // skip the counter so they must not be blocked by this check.
    const hasWin =
      quickState.game.currentRound?.win && quickState.game.currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin &&
      quickState.spin.spinComplete &&
      quickState.animations.counterAnimationStarted &&
      !quickState.animations.counterAnimationFinished;

    // Check for critical animation (expanding wilds, etc.)
    if (quickState.animations.criticalAnimationPlaying) {
      return; // Visual feedback already played
    }

    if (
      anyPopupActive ||
      sessionExpired ||
      quickState.userInterface.userInterfaceDisabled ||
      actionInProgressRef.current ||
      winNotFullyDisplayed
    ) {
      return; // Visual feedback already played
    }

    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) {
      return; // Visual feedback already played
    }

    // Action gating - block if action type doesn't match what's allowed
    if (action.type === "START_SPIN" && allowedActionRef.current !== "SPIN") {
      return;
    }
    if (action.type === "STOP_SPIN") {
      if (allowedActionRef.current === "STOP") {
        // Windup complete, execute stop immediately
      } else if (
        allowedActionRef.current === null &&
        isSpinningRef.current &&
        !windupCompleteRef.current
      ) {
        // During windup, queue the stop (only once)
        if (!pendingStopRef.current) {
          queuePendingStop();

          // Disable button immediately when stop is queued
          setActionInProgress(true);
        }
        return; // Don't execute yet (visual feedback already played at top)
      } else {
        // Not allowed at all
        return;
      }
    }

    executeButtonAction(action, "click");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    anyPopupActive,
    sessionExpired,
    resolveButtonAction,
    executeButtonAction,
    playButtonRef,
    flashBrightness,
    dispatch,
    device,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Touch pointer down handler - handles turbo activation on hold
  const handlePointerDown = useCallback(
    (event) => {
      const state = store.getState();

      // Block touch while counter is showing (PopiWinDisplay displays for 2 seconds)
      const counterIsShowing =
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      // TENSION SKIP: During active tension, each tap/click skips the current tension reel.
      // Works in ALL speed modes — tension is identical across auto/quick/turbo.
      // NOTE: No turboTensionEjectedRef guard here — that only blocks held spacebar.
      // A click/tap is always a fresh intentional action.
      if (pendingTensionRef?.current && state.spin.isSpinning) {
        event.stopPropagation();
        event.preventDefault();
        soundManager.play(SOUND_NAME.button_click);
        if (playButtonRef.current) {
          createButtonPressAnimation(playButtonRef.current, {
            targetWidth: buttonConfig.width,
            targetHeight: buttonConfig.height,
          });
        }
        handleManualStopRef.current();
        return;
      }

      if (
        touchPressed ||
        selectAnyPopupActive(state) ||
        state.game.sessionExpired ||
        state.userInterface.userInterfaceDisabled ||
        actionInProgressRef.current ||
        counterIsShowing
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      event.stopPropagation();
      event.preventDefault();

      // Block ALL actions if autoplay is active AND speed is turbo or quick
      if (
        state.settings.autoPlayActive &&
        (state.settings.autoPlaySpeed === "turbo" ||
          state.settings.autoPlaySpeed === "quick")
      ) {
        return;
      }

      // Handle autoplay: Simple one-action rule
      // If spinning, trigger manual stop ONCE, then clear hold state
      if (state.settings.autoPlayActive) {
        // If already pressed, ignore (prevents duplicate stops)
        if (touchPressed) {
          return;
        }

        // Mark as pressed to prevent duplicates
        touchPressed = true;
        touchStartTime = now;
        touchId = event.pointerId;

        // If spinning, trigger manual stop once
        if (state.spin.isSpinning && !state.spin.manualStop) {
          const action = resolveButtonAction();

          // Action gating for touch with pending stop support
          if (action.type === "STOP_SPIN") {
            if (allowedActionRef.current === "STOP") {
              // Windup complete, execute stop immediately
              executeButtonAction(action, "click");
            } else if (
              allowedActionRef.current === null &&
              state.spin.isSpinning &&
              !state.spin.windupComplete
            ) {
              // During windup, queue the stop (only once)
              if (!pendingStopRef.current) {
                queuePendingStop();

                // Add visual feedback when queuing stop
                if (playButtonRef.current) {
                  createButtonPressAnimation(playButtonRef.current, {
                    targetWidth: buttonConfig.width,
                    targetHeight: buttonConfig.height,
                  });
                }

                // Disable button immediately when stop is queued
                setActionInProgressRef.current(true);
              }
            }
          }
        }

        // Clear hold timer (no turbo during autoplay)
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }

        // Don't set up turbo timer - autoplay doesn't allow turbo
        return;
      }

      // Stop turbo if already active (from spacebar)
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
        return;
      }

      // Setup touch tracking (needed for both tap and hold detection)
      touchId = event.pointerId;
      touchPressed = true;
      touchStartTime = Date.now();

      // Only start turbo timer if animations are NOT active
      if (shouldAllowTurbo()) {
        // Start turbo after hold
        touchHoldTimer = setTimeout(() => {
          if (touchPressed && !spacebarTurboActiveRef.current) {
            // Double-check animations haven't started
            if (!shouldAllowTurbo()) {
              return;
            }

            const currentState = store.getState();

            // CRITICAL: Block turbo activation during autoplay
            if (currentState.settings.autoPlayActive) {
              touchPressed = false;
              touchStartTime = 0;
              touchId = null;
              return;
            }

            if (
              !currentState.spin.isSpinning &&
              !currentState.settings.autoPlayActive
            ) {
              // Allow turbo regardless of balance - API will handle insufficient credits
              lastActionTime = Date.now();
              startSpacebarTurboRef.current();
            }
          }
        }, FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      resolveButtonAction,
      executeButtonAction,
      shouldAllowTurbo,
      playButtonRef,
      buttonConfig.width,
      buttonConfig.height,
    ],
  );

  // Touch pointer up handler - stops turbo or performs quick tap action
  const handlePointerUp = useCallback(
    (event) => {
      if (!touchPressed || event.pointerId !== touchId) return;

      event.stopPropagation();
      event.preventDefault();

      const state = store.getState();

      // Handle autoplay: On release, just reset state - NO ACTIONS
      // Manual stop should ONLY happen on pointerDown, not on pointerUp
      // This prevents double-stop issues that cause infinite spin bugs
      if (state.settings.autoPlayActive) {
        // Clear timer and reset state
        if (touchHoldTimer) {
          clearTimeout(touchHoldTimer);
          touchHoldTimer = null;
        }
        touchPressed = false;
        touchStartTime = 0;
        touchId = null;

        return; // No turbo during autoplay, no actions on release
      }

      // Block clicks if there's a win that hasn't been fully displayed yet
      // Only applies when counterAnimationStarted is true — animated wins skip the counter.
      const hasWin =
        state.game.currentRound?.win && state.game.currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin &&
        state.spin.spinComplete &&
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      const holdDuration = Date.now() - touchStartTime;

      // Clear timer and reset state immediately to prevent stuck state
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // Clear turbo tension eject flag on touch release
      if (turboTensionEjectedRef?.current) {
        turboTensionEjectedRef.current = false;
      }

      if (actionInProgressRef.current) return;

      // If critical animation is playing AND turbo is active, stop turbo (button released during critical animation)
      if (
        state.animations.criticalAnimationPlaying &&
        spacebarTurboActiveRef.current
      ) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if critical animation is playing
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      // If win is being displayed AND turbo is active, stop turbo (button released during win display)
      if (winNotFullyDisplayed && spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if win is being displayed
      if (winNotFullyDisplayed) {
        return;
      }

      // Stop turbo if active
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      } else if (holdDuration < FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY) {
        // Quick tap - resolve and execute action
        const action = resolveButtonAction();

        // Action gating for touch
        if (
          action.type === "START_SPIN" &&
          allowedActionRef.current !== "SPIN"
        ) {
          return;
        }
        if (
          action.type === "STOP_SPIN" &&
          allowedActionRef.current !== "STOP"
        ) {
          return;
        }

        executeButtonAction(action, "click");
      }
    },
    [resolveButtonAction, executeButtonAction, turboTensionEjectedRef],
  );

  // Touch pointer cancel handler - cleanup on touch interruption
  const handlePointerCancel = useCallback((event) => {
    if (event.pointerId !== touchId) return;

    event.stopPropagation();
    event.preventDefault();

    // Clear timer and stop turbo if active
    if (touchHoldTimer) {
      clearTimeout(touchHoldTimer);
      touchHoldTimer = null;
    }
    if (spacebarTurboActiveRef.current) {
      stopSpacebarTurboRef.current();
    }

    // Reset touch state
    touchPressed = false;
    touchStartTime = 0;
    touchId = null;
  }, []);

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    const deviceConfig = PLAY_BUTTON_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig, dimensions, uiSymbolSize),
      y: posConfig.y(toolbar, buttonConfig, dimensions, uiSymbolSize),
    };
  }, [device, orientation, toolbar, buttonConfig, dimensions, uiSymbolSize]);

  // Get the appropriate icon texture based on state
  // Stop icon: when spinning and not complete, or when autoplay is active
  // Play icon: all other cases
  const shouldShowStop = autoPlayActive || (isSpinning && !spinComplete);
  const iconTexture = getUITexture(shouldShowStop ? "icon_stop" : "icon_play");

  // Hover container ref — hover scale goes on this, press goes on the sprite inside
  const hoverContainerRef = useRef(null);

  // Hover animation ref for cleanup
  const hoverAnimRef = useRef(null);

  // Hover effect handlers — scale only, on the container (not the sprite)
  const handlePointerOver = useCallback(() => {
    if (hoverContainerRef.current) {
      isHoveredRef.current = true;

      // Swap to hover texture
      if (playButtonRef.current) {
        const hoverTex = getUITexture(
          shouldShowStop ? "icon_stop_hover" : "icon_play_hover",
        );
        if (hoverTex) playButtonRef.current.texture = hoverTex;
      }

      if (hoverAnimRef.current) hoverAnimRef.current.pause();

      hoverAnimRef.current = anime({
        targets: hoverContainerRef.current.scale,
        x: 1.05,
        y: 1.05,
        duration: 150,
        easing: "easeOutQuad",
      });
    }
  }, [shouldShowStop, playButtonRef]);

  const handlePointerOut = useCallback(() => {
    if (hoverContainerRef.current) {
      isHoveredRef.current = false;

      // Revert to normal texture
      if (playButtonRef.current) {
        const normalTex = getUITexture(
          shouldShowStop ? "icon_stop" : "icon_play",
        );
        if (normalTex) playButtonRef.current.texture = normalTex;
      }

      if (hoverAnimRef.current) hoverAnimRef.current.pause();

      hoverAnimRef.current = anime({
        targets: hoverContainerRef.current.scale,
        x: 1.0,
        y: 1.0,
        duration: 150,
        easing: "easeOutQuad",
      });
    }

    // CRITICAL: Trigger liftoff if pointer leaves while button is held
    // This handles the edge case where user holds button then drags off it
    if (touchPressed) {
      // Clear timer and reset state immediately to prevent stuck state
      if (touchHoldTimer) {
        clearTimeout(touchHoldTimer);
        touchHoldTimer = null;
      }

      // Reset touch state
      touchPressed = false;
      touchStartTime = 0;
      touchId = null;

      // FORCEFULLY stop turbo if active (user dragged finger off button while turbo was running)
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playButtonRef, shouldShowStop]);

  // Sync texture when shouldShowStop changes while cursor is already on the button
  // (e.g., user clicks Play → spin starts → icon should switch to stop_hover)
  useEffect(() => {
    if (playButtonRef.current) {
      if (isHoveredRef.current) {
        const hoverTex = getUITexture(
          shouldShowStop ? "icon_stop_hover" : "icon_play_hover",
        );
        if (hoverTex) playButtonRef.current.texture = hoverTex;
      }
    }
  }, [shouldShowStop, playButtonRef]);

  // Prevent context menu on long press/touch-and-hold
  useEffect(() => {
    const preventContextMenu = (event) => {
      event.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", preventContextMenu, {
      passive: false,
    });
    return () => {
      document.removeEventListener("contextmenu", preventContextMenu);

      // Clear flash timeout to prevent memory leaks
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }
    };
  }, []);

  // Keyboard event handling - spacebar turbo activation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }


      // Block spacebar during free spins — free spins have their own controller
      if (selectFreeSpinActive(store.getState())) {
        return;
      }

      // Block spacebar during Royal Collect — RC has its own play button + spacebar handler
      if (selectRoyalCollectActive(store.getState())) {
        return;
      }

      event.preventDefault();

      // Block key repeat events (held spacebar auto-repeat)
      // Must be before tension skip to prevent held spacebar from auto-skipping all reels
      if (event.repeat) return;

      // PENDING STOP BYPASS: If user is spinning during windup, queue the stop IMMEDIATELY
      // This fires BEFORE the throttle so rapid SPIN→STOP is never swallowed
      if (!pendingStopRef.current && allowedActionRef.current === null) {
        const fastState = store.getState().spin;
        if (
          fastState.isSpinning &&
          !fastState.windupComplete &&
          !fastState.manualStop
        ) {
          queuePendingStop();
          setActionInProgressRef.current(true);
          soundManager.play(SOUND_NAME.button_click);
          if (playButtonRef.current) {
            createButtonPressAnimation(playButtonRef.current, {
              targetWidth: buttonConfig.width,
              targetHeight: buttonConfig.height,
            });
          }
          return;
        }
      }

      // EVENT-LEVEL THROTTLE: Instant return if throttle is active
      // Prevents performance degradation from rapid spacebar spam
      if (clickThrottleActive) {
        return; // Instant exit - zero overhead
      }

      // Set throttle flag IMMEDIATELY and schedule single reset
      clickThrottleActive = true;
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
      }
      clickThrottleTimeout = setTimeout(() => {
        clickThrottleActive = false;
        clickThrottleTimeout = null;
      }, MIN_ACTION_GAP);

      // Close menu if open (matches click behavior)
      dispatch(closeMenu());
      const updatedState = store.getState();

      // TENSION SKIP: During active tension, each press skips the current tension reel.
      // Works in ALL speed modes — tension is identical across auto/quick/turbo.
      if (pendingTensionRef?.current && updatedState.spin.isSpinning) {
        // Block if turbo was just ejected for tension (user still holding spacebar)
        if (turboTensionEjectedRef?.current) return;
        soundManager.play(SOUND_NAME.button_click);
        if (playButtonRef.current) {
          createButtonPressAnimation(playButtonRef.current, {
            targetWidth: buttonConfig.width,
            targetHeight: buttonConfig.height,
          });
        }
        handleManualStopRef.current();
        return;
      }

      // Determine what action will be taken
      const action = resolveButtonAction();

      // Block spacebar while counter is showing (PopiWinDisplay displays for 2 seconds)
      const counterIsShowing =
        updatedState.animations.counterAnimationStarted &&
        !updatedState.animations.counterAnimationFinished;

      // Check for critical animation (expanding wilds, etc.)
      if (updatedState.animations.criticalAnimationPlaying) {
        return;
      }

      if (
        selectAnyPopupActive(updatedState) ||
        updatedState.game.sessionExpired ||
        updatedState.userInterface.userInterfaceDisabled ||
        actionInProgressRef.current ||
        counterIsShowing
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) return;

      // Block ALL actions if autoplay is active AND speed is turbo or quick
      if (
        updatedState.settings.autoPlayActive &&
        (updatedState.settings.autoPlaySpeed === "turbo" ||
          updatedState.settings.autoPlaySpeed === "quick")
      ) {
        return;
      }

      // Handle autoplay: Simple one-action rule
      // If spinning, trigger manual stop ONCE, then clear hold state
      if (updatedState.settings.autoPlayActive) {
        // If already pressed, ignore (prevents duplicate stops from key repeat)
        if (spacePressed) {
          return;
        }

        // Mark as pressed to prevent duplicates
        spacePressed = true;
        spaceStartTime = now;

        // If spinning, trigger manual stop once
        if (updatedState.spin.isSpinning && !updatedState.spin.manualStop) {
          // Action gating for spacebar with pending stop support
          if (action.type === "STOP_SPIN") {
            if (allowedActionRef.current === "STOP") {
              // Windup complete, execute stop immediately
              executeButtonAction(action, "spacebar");
            } else if (
              allowedActionRef.current === null &&
              updatedState.spin.isSpinning &&
              !updatedState.spin.windupComplete
            ) {
              // During windup, queue the stop (only once)
              if (!pendingStopRef.current) {
                queuePendingStop();

                // Disable button immediately when stop is queued
                setActionInProgressRef.current(true);
              }
            }
          }
        }

        // Clear hold timer (no turbo during autoplay)
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }

        // Don't set up turbo timer - autoplay doesn't allow turbo
        return;
      }

      // Start turbo after hold (but not during animations)
      if (!spacePressed) {
        // Setup spacebar tracking (needed for both tap and hold detection)
        spacePressed = true;
        spaceStartTime = now;

        // Only start turbo timer if animations are NOT active
        if (shouldAllowTurbo()) {
          holdTimer = setTimeout(() => {
            if (spacePressed) {
              // Double-check animations haven't started
              if (!shouldAllowTurbo()) {
                return;
              }

              const currentState = store.getState();

              // CRITICAL: Block turbo activation during autoplay
              if (currentState.settings.autoPlayActive) {
                spacePressed = false;
                spaceStartTime = 0;
                return;
              }

              if (
                !currentState.spin.isSpinning &&
                !currentState.settings.autoPlayActive
              ) {
                // Allow turbo regardless of balance - API will handle insufficient credits
                lastActionTime = Date.now();
                startSpacebarTurboRef.current();
              }
            }
          }, FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY);
        }
      }
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }


      if (!spacePressed) return;

      const state = store.getState();

      // Handle autoplay: On release, just reset state - NO ACTIONS
      // Manual stop should ONLY happen on keyDown, not on keyUp
      // This prevents double-stop issues that cause infinite spin bugs
      if (state.settings.autoPlayActive) {
        // Clear timer and reset state
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        spacePressed = false;
        spaceStartTime = 0;

        return; // No turbo during autoplay, no actions on release
      }

      // Block clicks if there's a win that hasn't been fully displayed yet
      // Only applies when counterAnimationStarted is true — animated wins skip the counter.
      const hasWin =
        state.game.currentRound?.win && state.game.currentRound.win > 0;
      const winNotFullyDisplayed =
        hasWin &&
        state.spin.spinComplete &&
        state.animations.counterAnimationStarted &&
        !state.animations.counterAnimationFinished;

      const holdDuration = Date.now() - spaceStartTime;

      // Clear timer and reset state immediately to prevent stuck state
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      spacePressed = false;
      spaceStartTime = 0;

      // Clear turbo tension eject flag on key release
      if (turboTensionEjectedRef?.current) {
        turboTensionEjectedRef.current = false;
      }

      if (actionInProgressRef.current) {
        return;
      }

      // If critical animation is playing AND turbo is active, stop turbo (spacebar released during critical animation)
      if (
        state.animations.criticalAnimationPlaying &&
        spacebarTurboActiveRef.current
      ) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if critical animation is playing
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      // If win is being displayed AND turbo is active, stop turbo (button released during win display)
      if (winNotFullyDisplayed && spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
        return;
      }

      // Block other actions if win is being displayed
      if (winNotFullyDisplayed) {
        return;
      }

      // Stop turbo if active, otherwise perform quick tap action
      if (spacebarTurboActiveRef.current) {
        lastActionTime = Date.now();
        stopSpacebarTurboRef.current();
      } else if (holdDuration < FEATURES_CONFIG.TURBO.SPIN_SENSITIVITY) {
        // Quick tap - resolve and execute action
        const action = resolveButtonAction();

        // Action gating for spacebar with pending stop support
        if (
          action.type === "START_SPIN" &&
          allowedActionRef.current !== "SPIN"
        ) {
          return;
        }
        if (action.type === "STOP_SPIN") {
          if (allowedActionRef.current === "STOP") {
            // Windup complete, execute stop immediately
          } else if (
            allowedActionRef.current === null &&
            isSpinningRef.current &&
            !windupCompleteRef.current
          ) {
            // During windup, queue the stop (only once)
            if (!pendingStopRef.current) {
              queuePendingStop();

              // Add visual feedback when queuing stop
              if (playButtonRef.current) {
                createButtonPressAnimation(playButtonRef.current, {
                  targetWidth: buttonConfig.width,
                  targetHeight: buttonConfig.height,
                });
              }

              // Disable button immediately when stop is queued
              setActionInProgressRef.current(true);
            }
            return; // Don't execute yet
          } else {
            // Not allowed at all
            return;
          }
        }

        executeButtonAction(action, "spacebar");
      }
    };

    const handleWindowBlur = () => {
      // Cleanup on window blur
      if (holdTimer) {
        clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
      }
      spacePressed = false;
      spaceStartTime = 0;
    };

    // Safety net: visibilitychange fires on tab switch, mobile app switch,
    // and dev tools open — cases where window blur may not fire,
    // preventing turbo from getting stuck without a held spacebar.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleWindowBlur();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      // Cleanup: clear all timers, stop turbo, reset state
      if (holdTimer) clearTimeout(holdTimer);
      if (touchHoldTimer) clearTimeout(touchHoldTimer);
      if (spacebarTurboActiveRef.current) {
        stopSpacebarTurboRef.current();
      }

      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      spacePressed = false;
      spaceStartTime = 0;

      // Cleanup throttle timeout
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
        clickThrottleTimeout = null;
        clickThrottleActive = false;
      }
      holdTimer = null;
      touchPressed = false;
      touchStartTime = 0;
      touchHoldTimer = null;
      touchId = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    resolveButtonAction,
    executeButtonAction,
    shouldAllowTurbo,
    playButtonRef,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Auto-spin counter text style — theme blue, bold, centered on stop icon
  const autoSpinCounterStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: Math.floor(buttonConfig.width * 0.28),
        fontWeight: "700",
        fill: "#090f41",
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [buttonConfig.width],
  );

  // Show counter only during autoplay with remaining count > 0
  const showAutoSpinCounter =
    autoPlayActive && shouldShowStop && remainingAutoSpins > 0;

  // Infinity icon texture for infinite autoplay
  const infinityTexture = getUITexture("icon_infinity");
  const infinityIconSize = Math.floor(buttonConfig.width * 0.35);

  return (
    <Container
      name="secondary_button_play"
      x={buttonPosition.x}
      y={buttonPosition.y}
      alpha={featureButtonAlpha}
      eventMode={featureButtonInteractive ? "auto" : "none"}
    >
      {/* Hover container — scale animates here, independent of sprite press */}
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={playButtonRef}
          texture={iconTexture}
          name="button_play_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          onpointerdown={handlePointerDown}
          onpointerup={handlePointerUp}
          onpointercancel={handlePointerCancel}
          onpointertap={handleClick}
          alpha={1}
          resolution={2}
          accessible={true}
          accessibleTitle={
            shouldShowStop
              ? t("stop_button", { ns: "common" })
              : t("play_button", { ns: "common" })
          }
        />
        {/* Remaining auto-spin count — overlaid on stop icon */}
        {showAutoSpinCounter && remainingAutoSpins !== Infinity && (
          <Text
            text={String(remainingAutoSpins)}
            style={autoSpinCounterStyle}
            anchor={0.5}
            x={0}
            y={0}
            resolution={2}
          />
        )}
        {/* Infinity icon — overlaid on stop icon when infinite autoplay */}
        {showAutoSpinCounter &&
          remainingAutoSpins === Infinity &&
          infinityTexture && (
            <Sprite
              texture={infinityTexture}
              anchor={0.5}
              width={infinityIconSize}
              height={
                infinityIconSize *
                (infinityTexture.height / infinityTexture.width)
              }
              x={0}
              y={0}
              tint={0x090f41}
            />
          )}
      </Container>
    </Container>
  );
};

export default ButtonPlay;
